import { useEffect, useState } from "react";
import { Video, PlusCircle, Save, Trash2, Archive, Calendar, Clock } from "lucide-react";
import { api } from "@/lib/api";

function toLocalInput(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function fromLocalInput(v) {
    if (!v) return null;
    return new Date(v).toISOString();
}
function fmt(iso) {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

function CreateForm({ onCreated }) {
    const [form, setForm] = useState({
        title: "", scheduled_at_local: toLocalInput(new Date(Date.now() + 24 * 3600 * 1000).toISOString()),
        duration_minutes: 60, embed_url: "", visibility: "all_members", description_markdown: "",
    });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);

    async function create() {
        setBusy(true); setErr(null);
        try {
            if (!form.title.trim()) throw new Error("Title is required.");
            if (!form.embed_url.trim()) throw new Error("Embed URL is required.");
            if (!form.scheduled_at_local) throw new Error("Scheduled date/time is required.");
            const iso = fromLocalInput(form.scheduled_at_local);
            const res = await api.adminCreateLiveSession({
                title: form.title.trim(),
                scheduled_at: iso,
                duration_minutes: Number(form.duration_minutes) || 60,
                embed_url: form.embed_url.trim(),
                visibility: form.visibility,
                description_markdown: form.description_markdown.trim() || null,
            });
            onCreated?.(res);
            setForm({
                title: "", scheduled_at_local: toLocalInput(new Date(Date.now() + 24 * 3600 * 1000).toISOString()),
                duration_minutes: 60, embed_url: "", visibility: "all_members", description_markdown: "",
            });
        } catch (e) { setErr(e.message || "Failed to create session."); }
        finally { setBusy(false); }
    }

    return (
        <div className="st-surface p-6" data-testid="admin-live-create">
            <div className="tracking-caps text-[11px] font-semibold" style={{ color: "var(--st-royal)" }}>New live session</div>
            <div className="mt-4 grid gap-3 md:grid-cols-2">
                <div className="md:col-span-2">
                    <label className="st-label">Title</label>
                    <input className="st-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                           placeholder='e.g. "Week 47 — Live Q&A"' data-testid="admin-live-title" />
                </div>
                <div>
                    <label className="st-label">Scheduled at (your local time)</label>
                    <input type="datetime-local" className="st-input"
                           value={form.scheduled_at_local}
                           onChange={(e) => setForm({ ...form, scheduled_at_local: e.target.value })}
                           data-testid="admin-live-scheduled-at" />
                </div>
                <div>
                    <label className="st-label">Duration (min)</label>
                    <input type="number" min={15} max={480} className="st-input"
                           value={form.duration_minutes}
                           onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })}
                           data-testid="admin-live-duration" />
                </div>
                <div className="md:col-span-2">
                    <label className="st-label">Embed URL (Zoom / YouTube Live / etc.)</label>
                    <input className="st-input" value={form.embed_url} onChange={(e) => setForm({ ...form, embed_url: e.target.value })}
                           placeholder="https://zoom.us/j/1234567890 or https://www.youtube.com/embed/..."
                           data-testid="admin-live-embed-url" />
                </div>
                <div>
                    <label className="st-label">Visibility</label>
                    <select className="st-input" value={form.visibility}
                            onChange={(e) => setForm({ ...form, visibility: e.target.value })}
                            data-testid="admin-live-visibility">
                        <option value="all_members">All active members</option>
                        <option value="phase_3_only">Phase 3 only</option>
                    </select>
                </div>
                <div className="md:col-span-2">
                    <label className="st-label">Description (optional, markdown)</label>
                    <textarea rows={3} className="st-input" style={{ height: "auto", padding: "0.5rem 0.9rem" }}
                              value={form.description_markdown}
                              onChange={(e) => setForm({ ...form, description_markdown: e.target.value })}
                              data-testid="admin-live-description" />
                </div>
            </div>
            {err && <div className="mt-3 text-xs" style={{ color: "#B42318" }} data-testid="admin-live-create-err">{err}</div>}
            <div className="mt-4 flex justify-end">
                <button type="button" onClick={create} disabled={busy}
                        className="btn-royal inline-flex items-center gap-2"
                        data-testid="admin-live-create-submit">
                    <PlusCircle size={14} /> {busy ? "Creating…" : "Create session"}
                </button>
            </div>
        </div>
    );
}

function SessionRow({ row, onChanged }) {
    const [editing, setEditing] = useState(false);
    const [form, setForm] = useState({
        title: row.title,
        scheduled_at_local: toLocalInput(row.scheduled_at),
        duration_minutes: row.duration_minutes,
        embed_url: row.embed_url || "",
        visibility: row.visibility,
        description_markdown: row.description_markdown || "",
    });
    const [busy, setBusy] = useState(false);
    const [archiveUrl, setArchiveUrl] = useState(row.archive_url || "");
    const [err, setErr] = useState(null);

    async function save() {
        setBusy(true); setErr(null);
        try {
            const patch = {
                title: form.title,
                scheduled_at: fromLocalInput(form.scheduled_at_local),
                duration_minutes: Number(form.duration_minutes),
                embed_url: form.embed_url,
                visibility: form.visibility,
                description_markdown: form.description_markdown || null,
            };
            await api.adminPatchLiveSession(row.id, patch);
            setEditing(false);
            onChanged?.();
        } catch (e) { setErr(e.message || "Save failed."); }
        finally { setBusy(false); }
    }

    async function archive() {
        if (!archiveUrl.trim()) { setErr("Archive URL is required."); return; }
        setBusy(true); setErr(null);
        try {
            await api.adminArchiveLiveSession(row.id, archiveUrl.trim());
            onChanged?.();
        } catch (e) { setErr(e.message || "Archive failed."); }
        finally { setBusy(false); }
    }

    async function del() {
        if (!window.confirm(`Delete "${row.title}"? Reminders and viewer state are wiped.`)) return;
        setBusy(true); setErr(null);
        try {
            await api.adminDeleteLiveSession(row.id);
            onChanged?.();
        } catch (e) { setErr(e.message || "Delete failed."); }
        finally { setBusy(false); }
    }

    return (
        <div className="st-surface p-5" data-testid={`admin-live-row-${row.id}`}>
            <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                    <div className="text-xs" style={{ color: "var(--st-silver-500)" }}>
                        <Calendar size={11} className="inline mr-1" /> {fmt(row.scheduled_at)} · {row.duration_minutes} min · {row.visibility}
                    </div>
                    {!editing ? (
                        <div className="mt-1 font-serif text-xl" style={{ color: "var(--st-navy)" }} data-testid={`admin-live-title-${row.id}`}>
                            {row.title}
                        </div>
                    ) : (
                        <input className="st-input mt-2" value={form.title}
                               onChange={(e) => setForm({ ...form, title: e.target.value })} />
                    )}
                    {row.archive_url && !editing && (
                        <div className="mt-2 text-xs" style={{ color: "var(--st-royal)" }}>
                            <Archive size={11} className="inline mr-1" /> archived
                        </div>
                    )}
                </div>
                <div className="flex gap-2 flex-shrink-0">
                    {!editing ? (
                        <>
                            <button type="button" onClick={() => setEditing(true)} className="btn-outline-navy text-xs"
                                    data-testid={`admin-live-edit-${row.id}`}>Edit</button>
                            <button type="button" onClick={del} disabled={busy} className="btn-ghost text-xs"
                                    data-testid={`admin-live-delete-${row.id}`}>
                                <Trash2 size={11} className="inline mr-1" /> Delete
                            </button>
                        </>
                    ) : (
                        <>
                            <button type="button" onClick={() => setEditing(false)} className="btn-ghost text-xs">Cancel</button>
                            <button type="button" onClick={save} disabled={busy} className="btn-royal text-xs inline-flex items-center gap-1"
                                    data-testid={`admin-live-save-${row.id}`}>
                                <Save size={12} /> {busy ? "Saving…" : "Save"}
                            </button>
                        </>
                    )}
                </div>
            </div>

            {editing && (
                <div className="mt-4 grid gap-3 md:grid-cols-2">
                    <div>
                        <label className="st-label">Scheduled at</label>
                        <input type="datetime-local" className="st-input"
                               value={form.scheduled_at_local}
                               onChange={(e) => setForm({ ...form, scheduled_at_local: e.target.value })} />
                    </div>
                    <div>
                        <label className="st-label">Duration</label>
                        <input type="number" min={15} max={480} className="st-input"
                               value={form.duration_minutes}
                               onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} />
                    </div>
                    <div className="md:col-span-2">
                        <label className="st-label">Embed URL</label>
                        <input className="st-input" value={form.embed_url}
                               onChange={(e) => setForm({ ...form, embed_url: e.target.value })} />
                    </div>
                    <div>
                        <label className="st-label">Visibility</label>
                        <select className="st-input" value={form.visibility}
                                onChange={(e) => setForm({ ...form, visibility: e.target.value })}>
                            <option value="all_members">All active members</option>
                            <option value="phase_3_only">Phase 3 only</option>
                        </select>
                    </div>
                </div>
            )}

            {/* Archive section */}
            <div className="mt-4 pt-4" style={{ borderTop: "1px dashed var(--st-line)" }}>
                <div className="tracking-caps text-[10px] font-semibold mb-2" style={{ color: "var(--st-royal)" }}>
                    Archive recording
                </div>
                <div className="flex gap-2 items-center">
                    <input className="st-input flex-1" value={archiveUrl} placeholder="Recording URL (YouTube unlisted, Zoom cloud, etc.)"
                           onChange={(e) => setArchiveUrl(e.target.value)}
                           data-testid={`admin-live-archive-url-${row.id}`} />
                    <button type="button" onClick={archive} disabled={busy}
                            className="btn-outline-navy text-xs inline-flex items-center gap-1 flex-shrink-0"
                            data-testid={`admin-live-archive-save-${row.id}`}>
                        <Archive size={11} /> Set archive URL
                    </button>
                </div>
            </div>

            {err && <div className="mt-3 text-xs" style={{ color: "#B42318" }}>{err}</div>}
        </div>
    );
}

export default function AdminLiveSessions() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);

    async function load() {
        setLoading(true); setErr(null);
        try {
            const d = await api.adminListLiveSessions();
            setRows(d.sessions || []);
        } catch (e) { setErr(e.message || "Failed to load."); }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); }, []);

    return (
        <section className="max-w-5xl mx-auto px-6 py-10" data-testid="admin-live-sessions-page">
            <div>
                <div className="tracking-caps text-[11px] font-semibold" style={{ color: "var(--st-royal)" }}>Admin</div>
                <h1 className="font-serif text-4xl mt-2" style={{ color: "var(--st-navy)" }}>Live Sessions</h1>
                <p className="mt-2 text-sm max-w-2xl" style={{ color: "var(--st-ink-muted)" }}>
                    Schedule live sessions. Reminders fire automatically 24h and 1h before start. Post-session, paste the recording URL
                    into "Archive" to make it visible in members' archive list.
                </p>
            </div>

            <div className="mt-8 grid gap-6" data-testid="admin-live-sessions-list">
                <CreateForm onCreated={() => load()} />
                {loading && <div className="text-sm">Loading…</div>}
                {err && <div className="text-sm" style={{ color: "#B42318" }}>{err}</div>}
                {!loading && rows.length === 0 && (
                    <div className="st-surface p-6 text-sm text-center" style={{ color: "var(--st-ink-muted)" }}>
                        No sessions scheduled yet.
                    </div>
                )}
                {rows.map((r) => <SessionRow key={r.id} row={r} onChanged={() => load()} />)}
            </div>
        </section>
    );
}
